import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { HelpArticleSection } from './markdown-sections';

/**
 * TOC highlight: `IntersectionObserver` on each heading (no scroll listener), same active rule as
 * before (last heading whose top passed the sticky offset). `hashchange` for sidebar / deep links.
 * Offset aligns with `scrollMarginTop` in markdown-content.
 */
export function useHelpArticleActiveSection(sections: HelpArticleSection[]) {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const headerOffset = isMdUp ? 100 : 88;

    const [activeId, setActiveId] = useState('');

    const resolveFromHash = useCallback((): string => {
        const raw = window.location.hash.replace(/^#/, '');
        const id = raw ? decodeURIComponent(raw) : '';
        if (id && sections.some((s) => s.id === id)) {
            return id;
        }
        return sections[0]?.id ?? '';
    }, [sections]);

    const recomputeActiveFromGeometry = useCallback(() => {
        if (sections.length === 0) {
            return;
        }
        let current = sections[0]?.id ?? '';
        for (const s of sections) {
            const el = document.getElementById(s.id);
            if (!el) {
                continue;
            }
            if (el.getBoundingClientRect().top <= headerOffset + 2) {
                current = s.id;
            }
        }
        setActiveId((prev) => (prev === current ? prev : current));
    }, [sections, headerOffset]);

    useEffect(() => {
        if (sections.length === 0) {
            setActiveId('');
            return;
        }
        setActiveId(resolveFromHash());
    }, [sections, resolveFromHash]);

    useEffect(() => {
        if (sections.length === 0) {
            return undefined;
        }

        const onHash = () => {
            setActiveId(resolveFromHash());
        };
        window.addEventListener('hashchange', onHash);
        return () => {
            window.removeEventListener('hashchange', onHash);
        };
    }, [sections, resolveFromHash]);

    useLayoutEffect(() => {
        if (sections.length === 0) {
            return undefined;
        }

        let observer: IntersectionObserver | null = null;
        let cancelled = false;

        const attach = () => {
            const elements = sections.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
            if (elements.length === 0 || cancelled) {
                return false;
            }

            const onObserve: IntersectionObserverCallback = () => {
                recomputeActiveFromGeometry();
            };

            observer = new IntersectionObserver(onObserve, {
                root: null,
                rootMargin: '0px',
                threshold: [0, 0.25, 0.5, 0.75, 1],
            });
            for (const el of elements) {
                observer.observe(el);
            }
            recomputeActiveFromGeometry();
            return true;
        };

        if (!attach()) {
            const id = requestAnimationFrame(() => {
                if (!cancelled) {
                    attach();
                }
            });
            return () => {
                cancelled = true;
                cancelAnimationFrame(id);
                observer?.disconnect();
            };
        }

        return () => {
            cancelled = true;
            observer?.disconnect();
        };
    }, [sections, recomputeActiveFromGeometry]);

    return activeId;
}
